import React from 'react';
import styles from './RadarChart.module.css';

interface RadarDataPoint {
  label: string;
  value: number;   // 0-100
}

interface RadarChartProps {
  data: RadarDataPoint[];
  size?: number;    // 默认 260
  color?: string;   // 数据区域色
}

const RadarChart: React.FC<RadarChartProps> = ({
  data,
  size = 260,
  color = '#81c784',
}) => {
  const center = size / 2;
  const maxRadius = (size / 2) - 40; // 留边给标签
  const levels = 5; // 5 层网格

  // 计算顶点的坐标
  const getPoint = (index: number, value: number, radius: number) => {
    const angle = (Math.PI * 2 * index) / data.length - Math.PI / 2;
    const r = (value / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  };

  // 网格多边形
  const gridPolygons = Array.from({ length: levels }, (_, level) => {
    const r = ((level + 1) / levels) * maxRadius;
    return data
      .map((_, i) => {
        const p = getPoint(i, 100, r);
        return `${p.x},${p.y}`;
      })
      .join(' ');
  });

  // 数据多边形
  const dataPoints = data
    .map((d, i) => {
      const p = getPoint(i, d.value, maxRadius);
      return `${p.x},${p.y}`;
    })
    .join(' ');

  // 轴线
  const axes = data.map((_, i) => {
    const p = getPoint(i, 100, maxRadius);
    return { x1: center, y1: center, x2: p.x, y2: p.y };
  });

  // 标签位置（略外移）
  const labels = data.map((_, i) => {
    const p = getPoint(i, 100, maxRadius + 22);
    return { x: p.x, y: p.y, text: data[i].label };
  });

  // 薄弱模块索引（最低值）
  const minValue = Math.min(...data.map((d) => d.value));
  const weakIndices = data
    .map((d, i) => (d.value === minValue ? i : -1))
    .filter((i) => i !== -1);

  return (
    <div className={styles.container}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* 网格 */}
        {gridPolygons.map((pts, i) => (
          <polygon
            key={`grid-${i}`}
            points={pts}
            fill="none"
            stroke="#e8e0d9"
            strokeWidth={i === levels - 1 ? 1.5 : 0.8}
          />
        ))}

        {/* 轴线 */}
        {axes.map((a, i) => (
          <line
            key={`axis-${i}`}
            x1={a.x1}
            y1={a.y1}
            x2={a.x2}
            y2={a.y2}
            stroke="#e8e0d9"
            strokeWidth="0.8"
          />
        ))}

        {/* 数据区域 */}
        <polygon
          points={dataPoints}
          fill={color}
          fillOpacity="0.35"
          stroke={color}
          strokeWidth="2"
        />

        {/* 数据顶点 */}
        {data.map((d, i) => {
          const p = getPoint(i, d.value, maxRadius);
          const isWeak = weakIndices.includes(i);
          return (
            <circle
              key={`dot-${i}`}
              cx={p.x}
              cy={p.y}
              r={isWeak ? 5 : 4}
              fill={isWeak ? '#ff9800' : '#4caf50'}
              stroke="#fff"
              strokeWidth="1.5"
            />
          );
        })}

        {/* 标签 */}
        {labels.map((l, i) => {
          const isWeak = weakIndices.includes(i);
          return (
            <text
              key={`label-${i}`}
              x={l.x}
              y={l.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className={`${styles.label} ${isWeak ? styles.weakLabel : ''}`}
              fontSize="12"
            >
              {l.text}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default RadarChart;
